import eye from 'images/icons/company/eye.svg';
import close from 'images/icons/orders/close.svg';
import _ from 'lodash';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { hours_to_json, json_to_hours } from 'validators-web';
import { AAdditionalColumns, AdditionalColumn, AdditionalFormFields, CompanyAdditionalColumnValue, ReportColumnType, deleteAdditionalColumnValue, getAdditionnalFormFields, getClientCompanyAdditionalColumnsValues, updateAdditionalColumnValue } from '../../../atoms/additionalColumns';
import { AAdditionalFieldColumns } from '../../../atoms/additionalFieldColumns';
import { AAssortments } from '../../../atoms/assortment';
import { ACalculatedFields, CalculatedFieldTarget, CompanyCalculatedField, getCalculatedFieldsByCompany } from '../../../atoms/calculatedFields';
import { Group, GroupInside } from '../../../components_v2/Selector/actions';
import Switch from '../../../components_v2/Switch/Switch';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownSearchCompanies } from '../../../components_v2/dropdown/DropdownSearch';
import DropdownTagsCloud from '../../../components_v2/dropdown/DropdownTagsCloud';
import { blurOnEnterPressed } from '../../../components_v2/input/Input';
import { PopupContext } from '../../../components_v2/popup/Popup';
import { Dot } from '../../../styles/global/css/Dot';
import { DefaultTextDiv } from '../../../styles/global/css/GlobalText';
import { translateToString } from '../../../styles/global/translate';
import { FieldDisplayer } from '../../forms/FieldDisplayer';
import { standaloneColor } from '../../map/colorScale';
import Restricted from '../../permissions/Restricted';
import { ModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import { ValueInnerToNode } from '../../reports/interpretor/ReportInterpretor';
import { DateAccessor } from '../data/CompanyColumns';
import { BodyMainText, BodyMainTextEditable, ButtonAction } from '../style/PopupStyle';
import { CompanyDetailDisplayer, CompanyDetailDisplayerRowWrapper, CompanyDetailDisplayerWrapper, TitleAndChild } from './Detail';
import HoursDisplayer, { HoursJson } from './HoursDisplayer';
import { GreySidely2, LightGreySidely3 } from '../../../styles/global/css/Utils';

export const SyledCompanyTable = styled.div<{ width?: string }>`
	${p => p.width ? `width: ${p.width};` : ''}
	table {
		font-size: 13px;
		tr > td:first-child {
			color: ${GreySidely2};
			padding-right: 10px;
			text-align: right;
			&:after {
				content: ":";
			}
		}
		tr > td:last-child {
			width: 100%;
			display: flex;
			align-items: center;
		}
		tr > td {
			gap: 0.25em;
			padding-bottom: 10px;
			&:empty {
				&:before {
					content: '-';
				}
			}
		}
		tr:last-child > td {
			padding-bottom: 0px;
		}
	}
`;


const Close = styled.div`
	background-image: url(${close});
	background-size: 75%;
	cursor: pointer;
	background-repeat: no-repeat;
	background-position: center;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	&:hover {
		background-color: ${LightGreySidely3};
	}
`;

export default function AdditionalColumns(props: { clientCompanyId: number, isSelf: boolean, openSubCompany: React.Dispatch<React.SetStateAction<ModalState<number>>>, userCategories: Group[] }) {
	const additional_columns = useRecoilValue(AAdditionalColumns);
	const [additional_form_fields, setAdditionalFormFields] = React.useState<AdditionalFormFields[]>();
	const [companyAdditionalColumnsValues, setCompanyAdditionalColumnsValues] = React.useState<{ [key: number]: CompanyAdditionalColumnValue }>();
	const [companyCalculatedFields, setCompanyCalculatedFields] = React.useState<CompanyCalculatedField[]>();
	const aCalculatedFields = useRecoilValue(ACalculatedFields);
	const [localUserCategories, setLocalUserCategories] = React.useState<Group[]>();

	React.useEffect(() => {
		getAdditionnalFormFields(props.clientCompanyId).then(setAdditionalFormFields);
		getClientCompanyAdditionalColumnsValues(props.clientCompanyId).then(res => {
			setCompanyAdditionalColumnsValues(res.reduce((acc, val) => {
				acc[val.additional_column_id] = val;
				return acc;
			}, {}));
		});
		getCalculatedFieldsByCompany(props.clientCompanyId).then(setCompanyCalculatedFields);
	}, []);

	React.useEffect(() => {
		const localUserCategories = _.cloneDeep(props.userCategories);
		localUserCategories.forEach((category: Group) => {
			const insideWithName = category.inside.map((inside: GroupInside) => {
				const additionalColumn = additional_columns.find(c => c.id === inside.id);
				const cf = companyCalculatedFields?.find(c => c[0] === inside.id);
				const et = additional_form_fields?.find(et => et.field_id === inside.id);
				if (!additionalColumn && !cf && !et) {
					inside.ishidded = true;
				}
				return { ...inside, name: additionalColumn?.name ?? aCalculatedFields.find(c => c.id === inside.id)?.field_name ?? et?.name ?? '' };
			});
			insideWithName.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
			category.inside = insideWithName;
		});
		setLocalUserCategories(localUserCategories);
	}, [props.userCategories, additional_form_fields, additional_columns, companyCalculatedFields]);

	const onFieldUpdate = React.useCallback((value: CompanyAdditionalColumnValue) => {
		setCompanyAdditionalColumnsValues(prev => {
			if (!prev) return undefined;
			prev[value.additional_column_id] = value;
			return { ...prev };
		});
	}, []);

	return <SyledCompanyTable><FlexDiv flow="column" width='100%'>
		{localUserCategories && localUserCategories.length > 0 &&
			localUserCategories.map((category) => {
				if (category.inside.length === 0) return <></>;
				return <TitleAndChild title={category.id !== 1 ? category.name : translateToString(category.name)} key={category.id}>
					<CompanyDetailDisplayerWrapper>
						{category.inside.map((inside) => {
							const additionalColumn = additional_columns.find(c => c.id === inside.id);
							const cf = companyCalculatedFields?.find(c => c[0] === inside.id);
							const cfName = aCalculatedFields.find(c => c.id === inside.id)?.field_name;
							const et = additional_form_fields?.find(et => et.field_id === inside.id);
							if (inside.ishidded) return <></>;
							switch (inside.type_) {
								case 'AdditionalColumn':
									if (!additionalColumn) return <></>;
									return <AdditionalColumnDisplayer
										onDelete={console.log}
										onUpdate={onFieldUpdate}
										additional_column={additionalColumn}
										key={`AdditionalColumnDisplayer[${additionalColumn.id}]`}
										value={companyAdditionalColumnsValues?.[additionalColumn.id]}
										openSubCompany={props.openSubCompany}
										isSelf={props.isSelf}
										clientCompanyId={props.clientCompanyId}
									/>;
								case 'CalculatedField': {
									if (!cfName || !cf) return <></>;
									// les champs company_product vivent dans l'onglet Produits
									if (aCalculatedFields.find(c => c.id === inside.id)?.target === CalculatedFieldTarget.CompanyProduct) return <></>;
									if (cf[1] && typeof cf[1] === 'object' && 'products' in (cf[1] as object)) return <></>;
									// the field's map colours beside the value, when they tell by the value alone
									const cfColor = standaloneColor(aCalculatedFields.find(c => c.id === inside.id)?.display_options, 'CalculatedField', cf[1]);
									return <CompanyDetailDisplayer title={cfName} key={`calculated_field_columns[${cf[0]}]`}>
										{typeof cf[1] === 'object' && cf[1] ? <ValueInnerToNode value={cf[1] as ReportColumnType} color={cfColor} /> : '-'}
									</CompanyDetailDisplayer>;
								}
								case 'FormField':
									if (!et) return <></>;
									return <AdditionnalFormFieldDisplayer field={et} key={`additional_form_fields[${et.field_id}][${et.name}]`} />;
							}
						})}
					</CompanyDetailDisplayerWrapper>
				</TitleAndChild>;
			})
		}
	</FlexDiv>
	</SyledCompanyTable>;
}

const ADDITIONAL_COLUMNS_DROPDOWN_PROPS = {
	placeholder: '-',
	dropdownStyle: {
		inputMargin: '0',
		inputPadding: '0',
		containerHeight: 'unset',
		optionWidth: '100%',
		containerWidth: '100%',
		width: '100%',
		containerBorder: 'none',
		margin: '0',
	}
};

function AdditionalColumnDisplayer(props: {
	clientCompanyId: number,
	additional_column: AdditionalColumn,
	value: CompanyAdditionalColumnValue | undefined,
	openSubCompany: React.Dispatch<React.SetStateAction<ModalState<number>>>,
	isSelf: boolean,
	onUpdate: (value: CompanyAdditionalColumnValue | undefined) => void,
	onDelete?: () => void
}) {
	const { isChild } = React.useContext(PopupContext);
	const { value, openSubCompany, additional_column, clientCompanyId } = props;
	const [error, setError] = React.useState(false);
	let child: React.ReactNode = <></>;
	let val = value?.value;
	const assortments = useRecoilValue(AAssortments);
	const deleteValue = () => deleteAdditionalColumnValue(additional_column.id, clientCompanyId).then(() => props.onUpdate({ additional_column_id: additional_column.id, value: undefined }));
	const updateValue = (value) => {
		if (value === undefined) {
			return deleteValue();
		}
		return updateAdditionalColumnValue(additional_column.id, clientCompanyId, value).then(() => props.onUpdate({ additional_column_id: additional_column.id, value }));
	};

	// the column's map colours beside the value, when they tell by the value alone
	const valueColor = standaloneColor(additional_column.display_options, additional_column.type, value?.value);
	const dot = valueColor ? <Dot size='10px' color={valueColor} /> : undefined;

	switch (additional_column.type) {
		case 'Integer':
		case 'Number':
			if (typeof value?.value === 'number') {
				val = value.value.toString();
			} else if (typeof value?.value === 'string') {
				val = parseFloat(value.value).toString();
			}
		// eslint-disable-next-line no-fallthrough
		case 'String': {
			if (typeof val === 'string' || val === undefined || val === null) {
				child = <Restricted
					to={{ objectAction: 'UpdateCompany' }}
					isSelf={props.isSelf}
					fallback={<FlexDiv gap='6px' align='center'>{dot}<BodyMainText height='fit-content' marginLeft='0' minHeight='unset' display='block'>{val}</BodyMainText></FlexDiv>}
				>
					<FlexDiv gap='6px' align='center'>
						{dot}
						<BodyMainTextEditable
							height='fit-content'
							marginLeft='0'
							minHeight='unset'
							width='100%'
							suppressContentEditableWarning
							contentEditable
							onKeyDownCapture={blurOnEnterPressed}
							error={error}
							onInput={e => {
								let parsed;
								switch (additional_column.type) {
									case 'Integer':
										parsed = parseInt(e.currentTarget.innerText.trim());
										break;
									case 'Number': {
										parsed = parseFloat(e.currentTarget.innerText.trim().replace(',', '.'));
										break;
									}
									default:
										return;
								}
								if (isNaN(parsed)) {
									setError(true);
								} else {
									setError(false);
								}
							}}
							onBlur={(e) => {
								let parsed;
								if (e.currentTarget.innerText.trim() === '') {
									deleteValue();
									return;
								}
								switch (additional_column.type) {
									case 'Integer':
										parsed = parseInt(e.currentTarget.innerText.trim());
										break;
									case 'Number': {
										parsed = parseFloat(e.currentTarget.innerText.trim().replace(',', '.'));
										break;
									}
									default:
										parsed = e.currentTarget.innerText.trim();
										break;
								}
								updateValue(parsed);
							}}
						>
							{val}
						</BodyMainTextEditable>
						{val != undefined && <Close onClick={deleteValue} />}
					</FlexDiv>
				</Restricted>;
			} else {
				child = '-';
			}
			break;
		}
		case 'Select':
			child = <Restricted
				to={{ objectAction: 'UpdateCompany' }}
				fallback={value?.enriched_value ?? typeof value?.value === 'string' ? value?.value as string : '-'}
				isSelf={props.isSelf}
			>
				<FlexDiv>
					<Dropdown
						key={`DESTROYKEY[${additional_column.id}][${value?.value}]`}
						name=''
						{...ADDITIONAL_COLUMNS_DROPDOWN_PROPS}
						datalist={additional_column.data.map(s => ({ label: s, value: s }))}
						selectedValue={additional_column.data.map(s => ({ label: s, value: s })).find(s => s.value === value?.value)}
						onChange={value => updateValue(value?.value)}
					/>
					{val != undefined && <Close onClick={deleteValue} />}
				</FlexDiv>
			</Restricted>;
			break;
		case 'Boolean':
			child = <Restricted
				to={{ objectAction: 'UpdateCompany' }}
				isSelf={props.isSelf}
				fallback={value?.value === 'boolean' && value.value ? translateToString('true') : value?.value === 'boolean' ? translateToString('false') : '-'}
			>
				<FlexDiv>
					<Switch onChange={updateValue} value={typeof value?.value === 'boolean' ? value.value : false} />
					{val != undefined && <Close onClick={deleteValue} />}
				</FlexDiv>
			</Restricted>;
			break;
		case 'Date':
			child = typeof value?.value === 'string' ? <Restricted
				to={{ objectAction: 'UpdateCompany' }}
				isSelf={props.isSelf}
				fallback={<FlexDiv gap='6px' align='center'>{dot}<DateAccessor date={value.value} /></FlexDiv>}
			>
				<FlexDiv gap='6px' align='center'>
					{dot}
					<DateAccessor date={value.value} />
					{val != undefined && <Close onClick={deleteValue} />}
				</FlexDiv>
			</Restricted> : '-';
			break;
		case 'Catalogue':
			child = <Restricted
				to={{ objectAction: 'UpdateCompany' }}
				isSelf={props.isSelf}
				fallback={value?.enriched_value ?? '-'}
			>
				<FlexDiv>
					<Dropdown
						key={`DESTROYKEY[${additional_column.id}][${value?.value}]`}
						name=''
						{...ADDITIONAL_COLUMNS_DROPDOWN_PROPS}
						datalist={assortments.map(s => ({ label: s.name, value: s }))}
						selectedValue={assortments.map(s => ({ label: s.name, value: s })).find(s => s.value.id === value?.value)}
						onChange={value => updateValue(value?.value?.id)}
					/>
					{val != undefined && <Close onClick={deleteValue} />}
				</FlexDiv>
			</Restricted>;
			break;
		case 'ReportColumn':
			child = typeof value?.value === 'object' && value?.value ? <ValueInnerToNode value={value.value as ReportColumnType} color={valueColor} /> : '-';
			break;
		case 'Company': {
			const companyId = value?.value !== undefined && typeof value.value === 'number' ? value.value : undefined;

			child = <FlexDiv><BodyMainText noOverflow marginLeft='0' minHeight='unset' height='20px'>
				<DropdownSearchCompanies
					dropdownStyle={{ height: '20px' }}
					JSXButton={(handleChange) => <BodyMainTextEditable
						noOverflow height='fit-content' marginLeft='0' minHeight='unset'
						width='100%'
						suppressContentEditableWarning
						contentEditable
						onKeyDownCapture={blurOnEnterPressed}
						onInput={(e) => handleChange(e.currentTarget.innerText.trim())}
						display='block'
					>
						{value?.enriched_value ?? '-'}
					</BodyMainTextEditable>}
					selectedValue={companyId ? { label: value?.enriched_value ?? '-', value: { id: companyId } as never } : undefined}
					name={''}
					onChange={value => {
						updateValue(value?.value?.id)
							.then(() => {
								props.value && props.onUpdate({ ...props.value, value: value?.value?.id, enriched_value: value?.label as string });
							});
					}}
				/>
			</BodyMainText>
			{companyId && <ButtonAction width='20px' parent={CompanyDetailDisplayerRowWrapper} src={eye} onClick={() => isChild ? window.open(`companies?id=${companyId}`) : openSubCompany({ isOpen: true, data: companyId })} />}
			{val != undefined && <Close onClick={deleteValue} />}
			</FlexDiv>;
			break;
		}
		case 'MultiSelect': {
			let selected: string[] = [];
			try {
				if (Array.isArray(value?.value)) {
					selected = value.value;
				} else if (typeof value?.value === 'string') {
					selected = JSON.parse(value?.value ?? '[]');
				}
			} catch (_e) {
				selected = [];
			}

			child = <DropdownTagsCloud
				addImageSize='1.25em'
				NoneContainer={<DefaultTextDiv>-</DefaultTextDiv>}
				autoOptionUp
				horizontal
				noSearch
				addImageOnHover
				dropdownSearch
				selected={selected.map(s => ({ name: s, id: s }))}
				onChange={values => updateValue(values.map(t => t.name))}
				onDelete={deletedValue => {
					if (!value) return;
					const newValues = (value.value as string[])?.filter(s => s !== deletedValue);
					if (newValues.length === 0) {
						deleteValue();
						return;
					}
					updateValue(newValues);
				}}
				tags={additional_column.data.map(s => ({ name: s, id: s }))}
			/>;
			break;
		}
		case 'Hours': {
			const hours_json: HoursJson = !value?.value ? {} : hours_to_json(value.value);
			child = <HoursDisplayer
				hours_json={hours_json}
				onChange={(newHours) => {
					const newValue = json_to_hours(newHours);
					if (newValue === undefined || newValue.length === 0) {
						deleteValue();
					} else {
						updateValue(newValue);
					}
				}
				}
			/>;
		}
	}
	return <CompanyDetailDisplayer title={additional_column.name}>
		{child}
	</CompanyDetailDisplayer>;
}

function AdditionnalFormFieldDisplayer(props: { field: AdditionalFormFields }) {
	// the pinned field's map colours, shown beside the value when they tell by the value alone
	const pinned = useRecoilValue(AAdditionalFieldColumns).find(afc => afc.field_id === props.field.field_id);
	return <CompanyDetailDisplayer title={props.field.name}>
		<FieldDisplayer field_type={props.field.field_type} value={props.field.value} fieldId={props.field.field_id} displayOptions={pinned?.display_options} justifyLeft />
	</CompanyDetailDisplayer>;
}
